/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Nishant Singh
*/

import React, { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { View, StyleSheet, Dimensions } from 'react-native';
import { useDispatch } from 'react-redux';
import { BgWrapper } from '@components/screen/Layout';
import SociollaText from '@components/sociolla/SociollaText';
import * as Auth from '@/store/auth/actions';
import Toast from 'react-native-toast-message';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import InputPassword from '@components/form/InputPassword';
import { formGroup, formWrapper } from '@assets/styles/formStyles';
import { PASSWORD_REGEX } from '@constants';

const FormPasswordValidationSchema = Yup.object().shape({
  new_password: Yup.string()
    .min(8, 'Min. 8 characters.')
    .required('Password is required.')
    .matches(
      new RegExp(/^.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?].*$/),
      'Password must have at least 1 special character.',
    )
    .matches(new RegExp(/.*[A-Z].*/), 'Password must have at least 1 uppercase')
    .matches(
      new RegExp(PASSWORD_REGEX),
      'Password must have at least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character (!@#$%^&*.), no spaces, and no character repeated 3+ times in a row',
    ),
  confirm_password: Yup.string()
    .min(8, 'Min. 8 characters.')
    .required('Confirm password is required.')
    .oneOf([Yup.ref('new_password'), null], 'Confirm password must match.')
    .matches(
      new RegExp(/^.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?].*$/),
      'Confirm password must have at least 1 special character.',
    )
    .matches(
      new RegExp(/.*[A-Z].*/),
      'Confirm password must have at least 1 uppercase',
    )
    .matches(
      new RegExp(PASSWORD_REGEX),
      'Password must have at least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character (!@#$%^&*.), no spaces, and no character repeated 3+ times in a row',
    ),
});

const ResetPasswordScreen = (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const dispatch = useDispatch();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [isLoading, setIsLoading] = useState(false);
  const userId = props?.route?.params?.user_id;
  const passwordKey = props?.route?.params?.key;
  const passwordToken = props?.route?.params?.token;

  return (
    <BgWrapper testID="reset-password">
      <View testID="reset-password-wrapper" style={styles.loginWrapper}>
        <SociollaText testID="reset-password-title" style={styles.loginTitle}>
          Reset Password
        </SociollaText>
        <Formik
          initialValues={{
            new_password: '',
            confirm_password: '',
          }}
          validationSchema={FormPasswordValidationSchema}
          onSubmit={async (payload) => {
            setIsLoading(true);
            try {
              if (!userId || !passwordKey || !passwordToken) {
                Toast.show({
                  text1: 'Reset link is invalid or expired',
                  type: 'error',
                });
                setIsLoading(false);
                return;
              }
              const response = await dispatch(
                Auth.resetPassword({
                  user_id: userId,
                  password_key: passwordKey,
                  password_token: passwordToken,
                  new_password: payload.new_password,
                  confirm_password: payload.confirm_password,
                }),
              );
              if (response.success) {
                Toast.show({
                  text1: 'Password reset successfully',
                  type: 'success',
                });
                props.navigation.navigate('LoginScreen');
              } else {
                if (response.errorCode === 'INVALID_PASSWORD') {
                  Toast.show({
                    text1: response.message,
                    type: 'error',
                    props: {
                      style: { maxWidth: '100%' },
                    },
                  });
                } else if (
                  response.errorCode === 'BAD_REQUEST' &&
                  response.message === 'Reset password link is not valid now!'
                ) {
                  Toast.show({
                    text1:
                      'Reset password link has expired. Please request a new password reset link',
                    type: 'error',
                    props: { text1NumberOfLines: 2 },
                  });
                } else {
                  Toast.show({ text1: 'Something went wrong', type: 'error' });
                }
              }
              setIsLoading(false);
            } catch (err) {
              setIsLoading(false);
              Toast.show({
                text1: err?.response?.data?.message || 'Something went wrong',
                type: 'error',
              });
            }
          }}>
          {({ setFieldValue, handleSubmit, errors, touched, values }) => (
            <>
              <View style={{ ...formWrapper, marginBottom: 20 }}>
                <View style={formGroup}>
                  <InputPassword
                    testID="reset-password-new"
                    label="Password"
                    value={values.new_password}
                    isRequired={true}
                    placeholder=""
                    hasError={
                      errors.new_password && touched.new_password
                        ? errors.new_password
                        : null
                    }
                    onChangeText={(val) => setFieldValue('new_password', val)}
                    onSubmitEditing={handleSubmit}
                  />
                </View>

                <View style={[formGroup, { marginTop: 10 }]}>
                  <InputPassword
                    testID="reset-password-confirm"
                    label="Confirm Password"
                    value={values.confirm_password}
                    isRequired={true}
                    placeholder=""
                    hasError={
                      errors.confirm_password && touched.confirm_password
                        ? errors.confirm_password
                        : null
                    }
                    onChangeText={(val) =>
                      setFieldValue('confirm_password', val)
                    }
                    onSubmitEditing={handleSubmit}
                  />
                </View>
              </View>
              <View
                style={{
                  width: '100%',
                  paddingHorizontal: 6,
                  paddingVertical: 10,
                }}>
                <Button
                  testID="reset-password-submit-button"
                  onPress={handleSubmit}
                  disabled={
                    isLoading ||
                    !values.new_password ||
                    !values.confirm_password
                  }
                  title={isLoading ? 'LOADING...' : 'SET PASSWORD'}
                />
              </View>
            </>
          )}
        </Formik>
      </View>
    </BgWrapper>
  );
};

const styles = StyleSheet.create({
  loginWrapper: {
    width: isWeb ? 400 : Dimensions.get('window').width,
    marginHorizontal: 'auto',
    paddingVertical: isWeb ? 20 : 40,
    paddingHorizontal: 16,
    backgroundColor: Colors.white,
    borderTopRightRadius: isWeb ? 5 : 20,
    borderTopLeftRadius: isWeb ? 5 : 20,
    borderBottomRightRadius: isWeb ? 5 : 0,
    borderBottomLeftRadius: isWeb ? 5 : 0,
  },
  loginTitle: {
    fontSize: 26,
    fontFamily: 'figtree-semiBold',
    letterSpacing: 0.5,
    marginBottom: 20,
  },
});

export default React.memo(ResetPasswordScreen, (prev, next) => prev == next);
